import { useContext, useEffect, useRef, useState } from 'react';
import { MenuItem, TextField } from '@mui/material';

import { CompanyContext } from '../../../context/CompanyContext';
import { GroupContext } from '../../../context/GroupContext';
import { fetchEntities } from '../../../services/syntiro_honey/data_ingestion.service';
import { EntityLookupItem } from '../../../types/syntiro_honey';

/**
 * Shrinks the label permanently so the field reads consistently beside the
 * context form's fields (see FileContextForm's SHRUNK_LABEL rationale).
 */
const SHRUNK_LABEL = { inputLabel: { shrink: true } } as const;

interface ClientFieldProps {
  /** The chosen client's company id ('' = not chosen). */
  companyId: string;
  /** Called with the next company id (auto-assignment or a manual pick). */
  onCompanyIdChange: (companyId: string) => void;
  /** Disable interaction (e.g. while an upload is in flight). */
  disabled?: boolean;
}

/**
 * The upload's Client field — batch-wide, exactly once per upload. Client is NOT
 * a hint: it is the one field with a hard consequence (entity resolution is
 * scoped to it, and a hint can never resolve into another client's entities), so
 * it lives beside the file list rather than inside any context form (Maxine R4 ④
 * removed the batch context form this field used to sit in).
 *
 * Four states (OQ24 / Maxine ②): ASSIGNED — the logged-in COMPANY itself carries
 * a `client_code` matching an ingestion client, and the field locks, because a
 * client user must never file records under another client; DEFAULTED — no
 * company-level code, but the parent GROUP's does match one; the field
 * pre-fills but stays editable, because a group can span more than one
 * ingestion client and its code is a default, not the authority (Josef,
 * 2026-08-12) — locking on it would misfile every upload for a company the
 * group-level code does not actually name; manual pick — no code, or a code
 * matching nothing; legibly degraded — the client list could not be loaded
 * (uploads never block on it). The client IS the company (Maxine, 2026-08-12).
 * @param {string} companyId The chosen client's company id.
 * @param {(companyId: string) => void} onCompanyIdChange Company-change handler.
 * @param {boolean | undefined} disabled Disable interaction.
 * @returns {JSX.Element | null}
 */
export default function ClientField({ companyId, onCompanyIdChange, disabled }: ClientFieldProps) {
  // Best-effort, legibly degraded: an unreachable catalogue renders the field
  // disabled with an explanation, not silently absent. Only a successfully-
  // loaded-but-empty catalogue hides it (then there is genuinely nothing to choose).
  const [companies, setCompanies] = useState<Array<EntityLookupItem>>([]);
  const [companiesUnavailable, setCompaniesUnavailable] = useState<boolean>(false);
  useEffect(() => {
    let cancelled = false;
    fetchEntities()
      .then((entities) => {
        if (!cancelled) {
          setCompanies(entities.filter((entity) => entity.kind === 'COMPANY'));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCompaniesUnavailable(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const { company } = useContext(CompanyContext);
  const { group } = useContext(GroupContext);
  // Only the COMPANY's own code is authoritative; a match found via the GROUP's
  // code is a default (see the states above), so the two must stay distinguishable.
  const company_locked = company?.client_code != null;
  const login_client_code = company?.client_code ?? group?.client_code ?? null;
  const assigned_company =
    login_client_code !== null
      ? (companies.find((candidate) => candidate.clientCode === login_client_code) ?? null)
      : null;

  useEffect(() => {
    if (assigned_company === null) {
      return;
    }
    if (company_locked) {
      // Authoritative — hold the field on it, undoing any drift.
      if (companyId !== assigned_company.companyId) {
        onCompanyIdChange(assigned_company.companyId);
      }
    }
  }, [assigned_company, company_locked, companyId, onCompanyIdChange]);

  const defaultApplied = useRef(false);
  useEffect(() => {
    if (
      assigned_company !== null &&
      !company_locked &&
      !defaultApplied.current &&
      companyId === ''
    ) {
      defaultApplied.current = true;
      onCompanyIdChange(assigned_company.companyId);
    }
  }, [assigned_company, company_locked, companyId, onCompanyIdChange]);

  if (companies.length === 0 && !companiesUnavailable) {
    return null;
  }
  if (assigned_company !== null && company_locked) {
    return (
      // Locked, not hidden: the uploader must SEE which client will own the
      // records, and see why they cannot change it here.
      <TextField
        size="small"
        label="Client"
        helperText="Taken from the company you are logged in as."
        value={assigned_company.name}
        disabled
        sx={{ minWidth: 320 }}
        slotProps={SHRUNK_LABEL}
      />
    );
  }
  return (
    <TextField
      select
      size="small"
      label="Client"
      helperText={
        companiesUnavailable
          ? 'Client list unavailable — uploads still work'
          : assigned_company !== null
            ? 'Defaulted from your login group — change it if this upload is for a different client.'
            : login_client_code !== null
              ? "Your login company isn't linked here yet — pick the client manually."
              : "Limits matching to this client's sites and divisions."
      }
      value={companyId}
      onChange={(event) => onCompanyIdChange(event.target.value)}
      disabled={disabled || companiesUnavailable}
      sx={{ minWidth: 320 }}
      slotProps={SHRUNK_LABEL}
    >
      <MenuItem value="">All clients</MenuItem>
      {companies.map((client) => (
        <MenuItem key={client.companyId} value={client.companyId}>
          {client.name}
        </MenuItem>
      ))}
    </TextField>
  );
}
